import { Form, Typography, Button } from 'antd'
import _ from "lodash";
import { useForm } from 'antd/lib/form/Form'
import BaseModal from 'components/BaseModal'
import Vocabulary from 'components/Vocabulary'
import { MODAL_SIZE_LARGE, MODAL_SIZE_SMALL } from 'constants/Layout'
import React, { useEffect, useMemo, useState } from 'react'
import { useImperativeHandle } from 'react'
import { forwardRef, useRef, useCallback } from 'react'
import IntegrationsAPI from 'restAPI/IntegrationsAPI'
import ProductAPI from 'restAPI/ProductAPI'
import UserAPI from 'restAPI/UserAPI'
import { FieldTypes, generateFormFields, nameRegexValidator } from 'utils/formUtils'
import ModalUtil from 'utils/modalUtil'
import moment from 'moment';
import { useActiveUserContext } from 'contexts/ActiveUserContext';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { DeleteIcon } from 'components/Icons';
import { hasPermission, isSuperOrTenantAdmin } from 'utils/authService';
import { PermissionSetMap } from 'constants/RBACConstants';

const {Link} = Typography;

export default forwardRef(function ArmorCodeApiForm({afterClose,configData, ...props}, prevRef) {
    const [form] = useForm();
    const activeUser = useActiveUserContext();
    const [created, setCreated] = useState(false);
    const [createdData, setCreatedData] = useState();
    const [list, setList] = useState([])
    const ref = useRef();
    const isEdit = !_.isUndefined(configData);
    const featureFlags = useFeatureFlag();

    const [userFieldEnabled, setUserFieldEnabled] = useState(false);
    const toggleUserFieldEnabled = useCallback(() => {
        setUserFieldEnabled(f => !f);
    }, []);

    const [disableAccountLevelAccess, setDisableAccountLevelAccess] = useState(featureFlags.ENABLE_FOR_REPORT_INGESTION_TOKEN ? isEdit : true)
    const [data, setData] = useState(() => {
        const _data = getInitialData(configData);
        if (!isEdit || form.getFieldValue("accountLevelToken")) {
            _data.product = "all";
         }
         return _data;
;
    });
    
    
    useImperativeHandle(
        prevRef,
        () => ref.current
    )

    const appendAll = useCallback((list = []) => ([{ label: "All", value: "all" }, ...list]), []);

    useEffect(()=>{
      const {promise, cancel} = IntegrationsAPI.getAllAPIKeys();
      promise.then(setList)
      return cancel
    },[])

   

    const generateToken = async () => {
        await form.validateFields();
        const name = form.getFieldValue('tokenName');
        const values = form.getFieldsValue();
        const _expiryPeriod = form.getFieldValue('expiry');
        const expiry = _expiryPeriod === -1 ? null : moment().add(_expiryPeriod, 'days').endOf('D').valueOf();
        const apiTokenType = form.getFieldValue('apiTokenType')
        const productIds = form.getFieldValue('product') === "all" ? [] :  form.getFieldValue('product') === undefined ? null : [form.getFieldValue('product')];
        const accessOnAllProducts =  form.getFieldValue('product') === undefined ? true : form.getFieldValue('product') === "all"
        const role = form.getFieldValue('role') === undefined ? null :  form.getFieldValue('role');
        const editId = configData?.data.id;
        const accountLevelToken = form.getFieldValue("accountLevelToken");
        const { userId } = values;
        const data = await (isEdit ? IntegrationsAPI.updateAPIKey : IntegrationsAPI.createNewAPIKey )({ 
            expiry, 
            role,
            name, 
            apiTokenType,
            productIds, 
            accessOnAllProducts,
            editId, 
            accountLevelToken,
            userId
        }).promise;
        setCreatedData(data)
        setCreated(true)
        ref.current.close();
        !isEdit && ModalUtil.show({
            content: (
                <BaseModal
                    modalProps={{
                        width: MODAL_SIZE_SMALL, 
                        title: "API key generated!",
                        footer:[
                            <div key = {"api-key"}>
                                <Button form="myForm" type="primary" key="submit" htmlType="submit" className="m-t-m">
                                    <Link href={`data:text/plain;charset=utf-8,${data.apiKey}`} download={`ArmorCode_API_key.txt`}>Download API key</Link>
                                </Button>
                            </div>]
                    }}
                >
                    <>
                        Key: <Typography.Text copyable={{ text: data.apiKey }} >***************{data.apiKey.substr(data.apiKey.length - 4, 4)}</Typography.Text>
                    </>
                </BaseModal>
            )
        })
    }

    const baseModalProps = {
        title: isEdit ? "Edit ArmorCode token" : "ArmorCode API token generation",
        width: MODAL_SIZE_LARGE,
        okText: isEdit ? "Save" : "Generate token",
        onOk: generateToken,
        disabled: !hasPermission(isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS),
        onCancel: props.onCancel,
    }

    const assignableRolesParams = useMemo(() => {
        const { sessionData: { tenantScope, assoiatedTeamRoleMap={} }, role} = activeUser;
        let params = {};
        if (!tenantScope) {
                const [team] = assoiatedTeamRoleMap[role];
                params['teamId'] = team.id
        }
        return params;
    }, [activeUser])

    const handleAPITokenChange = useCallback((value)=>{
        let allowedTokens = ["USER", "CICD_TOOL"];
        if(featureFlags.ENABLE_FOR_REPORT_INGESTION_TOKEN){
            allowedTokens.push("APP_SEC_TOOL")
        }

        if(allowedTokens.indexOf(value) === -1 && !disableAccountLevelAccess){
            setDisableAccountLevelAccess(true);
            form.setFieldsValue({accountLevelToken: false})
            setData(data=> ({...data, accountLevelToken: false}))
        }else if(allowedTokens.indexOf(value) !== -1){
            setDisableAccountLevelAccess(false);
        }
        if(value === "WEB_AGENT"){
            setUserFieldEnabled(false);
        }

    }, [disableAccountLevelAccess, form, featureFlags.ENABLE_FOR_REPORT_INGESTION_TOKEN])

    const filteredApiTokenTypeList = isSuperOrTenantAdmin() ? API_TOKEN_TYPE_LIST : API_TOKEN_TYPE_LIST.filter(item => item.value !== "WEB_AGENT");

    const fields = useMemo(() => {
        return [
            ...getUserFields({
                userFieldEnabled,
                toggleUserFieldEnabled,
                isEdit,
                data
            }),
            ...getCreatedForFields({
                isEdit,
            }), 
            {
            name: "tokenName",
            label: "Token Name",
            NoDefaultLengthRule: true,
            required: true,
            props: {
                autoFocus: true
            },
            rules:[
                nameRegexValidator({regex: "", min: 3}),
                {
                    validator: (_,value) =>{
                        if(value){
                            if(!isEdit){
                                for(const object of list){
                                    if(object.name === value){
                                        return Promise.reject("Token Name already exists")
                                    }
                                }
                            }
                    return Promise.resolve()
                }
                else{
                    return Promise.reject()
                }
            }
          }],
          disabled: isEdit,
        },
    {
        name: "apiTokenType",
        label: "API key type",
        type: FieldTypes.SELECT,
        options: filteredApiTokenTypeList,
        disabled: isEdit,
        props:{
            onChange: handleAPITokenChange
        }
    }, {
        name: "expiry",
        label: "Expiry",
        type: FieldTypes.SELECT,
        options: [{ label: "Never", value: -1 }, { label: "3 months", value: 90 }, { label: "6 months", value: 180 }, { label: "1 year", value: 365 }],
        disabled: isEdit,
    },{
        name: "product",
        label: <Vocabulary name="product" />,
        type: FieldTypes.SELECT,
        showSearch: true,
        props: {
            api: ProductAPI.getAllQuick,
            optionsMutator: appendAll,
            allowClear: true
        },
        disabled: data.apiTokenType === "OKTA_SCIM" || data.accountLevelToken  || data.apiTokenType === "WEB_AGENT"
    },
    {
        name: "role",
        label: "Role",
        type: FieldTypes.SELECT,
        props: {
            api: UserAPI.getAssignableRoles,
            apiParams: assignableRolesParams,
            parseSelectOptions: (option) => {
                const roles = parseUserRoleSelecter(option); 
                if(data.apiTokenType !== "USER"){
                    form.setFieldsValue({"role": null});
                }
                return roles;
            },
            allowClear: true,
            setFormListItemProps: (field) => {
                const isRequired = data.apiTokenType === "USER";
                if (isRequired) {
                  return {
                    rules: [{
                      required: true,
                      message: <>{field.label} is required.</>
                    }]
                  }
                } else {
                  return {
                    validateStatus: 'success',
                    help: ''
                  };
                }
              }
        },
        disabled: data.apiTokenType !== "USER",
    },
    {
        name: "accountLevelToken",
        label: "Account Level Access",
        help: (featureFlags.ENABLE_FOR_REPORT_INGESTION_TOKEN && data.apiTokenType === "APP_SEC_TOOL") && "Account level Access only supported for v2 APIs",
        type: FieldTypes.SWITCH,
        disabled: disableAccountLevelAccess,
        props: {
            onChange: (value) => {
                if(value && ["USER", "APP_SEC_TOOL"].indexOf(data.apiTokenType) !== -1){
                    form.setFieldsValue({"product": "all"})
                }
            }
        }
    }
]
    }, [
        userFieldEnabled,
        toggleUserFieldEnabled,
        isEdit,
        appendAll,
        list,
        assignableRolesParams,
        disableAccountLevelAccess,
        handleAPITokenChange,
        featureFlags.ENABLE_FOR_REPORT_INGESTION_TOKEN,
        form,
        filteredApiTokenTypeList,
        data
    ])
    return (
        <BaseModal
            modalProps={baseModalProps}
            ref={ref}
            afterClose={()=>{ afterClose(created, createdData) }}
        >
            <Form
                form={form}
                layout="vertical"
                onValuesChange={(_, values) => setData({...data, ...values})}
                initialValues={data}
            >
                {generateFormFields(fields, 12)}
            </Form>
        </BaseModal>
    )
})

export const API_TOKEN_TYPE_LIST = [{ label: "Report Ingestion", value: "APP_SEC_TOOL" }, { label: "CI/CD", value: "CICD_TOOL" },{label: 'API', value:'USER'}, {label: 'GitHub Enterprise Token', value: 'GITHUB_ENTERPRISE_TOKEN'}, {label: "SCIM", value: "OKTA_SCIM"}, {label: "Web Agent", value: "WEB_AGENT"}]

const parseUserRoleSelecter = (data) =>{
    const roles = data.map(item => {
        return {label: item.role, value: item.id}
    })
    return roles;
}

function getInitialData(data = {}) {

    if(_.isEmpty(data)) {
        return {
            expiry: -1,
            apiTokenType: "APP_SEC_TOOL"
        }
    }
    const filterData = {
        id: data.data.id,
        expiry: data.data.expiry ? getDifferenceFromToday(data.data.expiry) : -1,
        tokenName: data.data.name,
        apiTokenType: data.data.apiKeyType,
        product: _.isEmpty(data.data.products) ? "all" : data.data.products[0].id,
        role: data.data.role?.id,
        accountLevelToken: data.data.accountLevelToken,
        user: data.data.user?.name
    };
    if(data.data.apiKeyType === "OKTA_SCIM"){
        filterData.product = "all";
    }
    return filterData;
}

function getDifferenceFromToday(miliseconds) {
    return moment(miliseconds).startOf("D").diff(moment(), 'days');
}

function getUserFields({ userFieldEnabled, toggleUserFieldEnabled, isEdit, data }) {
    if(isEdit || !isSuperOrTenantAdmin()){
        return [];
    }
    if (!userFieldEnabled && data.apiTokenType !== "WEB_AGENT") {
        return [
            {
                name: "",
                type: FieldTypes.LABEL,
                value: (
                <Link onClick={toggleUserFieldEnabled}>
                    Want to create API token for another user?
                </Link>
                ),
            },
            {
                type: FieldTypes.LABEL,
                value: "",
                colSpan: 12,
            },
        ];
    }
    return data.apiTokenType !== "WEB_AGENT" ? [
        {
            label: "Select User",
            name: "userId",
            type: FieldTypes.USER_EMAIL,
            props: {
                labelKey: "displayName",
                valueKey: "id",
                mode: "single"
            },
            colSpan: 11
        },
        {
            label: " ",
            render: () => <DeleteIcon onClick={toggleUserFieldEnabled} className="clickable danger" />,
            colSpan: 12,
            style: {
                marginLeft: -40
            }
        },
    ] : [];
}

function getCreatedForFields({ isEdit }) {
    if(!isEdit){
        return [];
    }

    return[{
            label: "Created For",
            name: "user",
            type: FieldTypes.USER_EMAIL,
            props: {
                labelKey: "displayName",
                valueKey: "id",
                mode: "single"
            },
            disabled: isEdit
        }]
}
